import { create } from 'zustand';
import { clearSessionDrafts } from '@/hooks/useSessionDraft';
import { ApiRequestError } from '@/lib/apiClient';
import { tokenStore } from '@/lib/tokenStore';
import type { AuthUser } from '@/types/api';
import { authApi } from '../api/auth.api';
import type { LoginFormValues } from '../schemas/login.schema';

/**
 * Session state for the whole app.
 *
 * Only the user object lives here. The access token deliberately stays in
 * `tokenStore` (a plain module variable) — see the note in that file.
 */
interface AuthState {
  user: AuthUser | null;
  /** True until the initial refresh attempt finishes, so routes do not flash. */
  isBootstrapping: boolean;
  isAuthenticated: boolean;

  login: (credentials: LoginFormValues) => Promise<AuthUser>;
  logout: () => Promise<void>;
  /** Restores a session from the httpOnly refresh cookie after a page reload. */
  bootstrap: () => Promise<void>;
  clearSession: () => void;
  setUser: (user: AuthUser) => void;
}

/** The page load's one session restore, shared by every call to `bootstrap`. */
let bootstrapping: Promise<void> | null = null;

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isBootstrapping: true,
  isAuthenticated: false,

  login: async (credentials) => {
    const { user, accessToken } = await authApi.login(credentials);
    tokenStore.set(accessToken);
    set({ user, isAuthenticated: true, isBootstrapping: false });
    return user;
  },

  logout: async () => {
    try {
      await authApi.logout();
    } catch {
      // The session may already be gone server-side; clearing locally is
      // what actually matters, so never block the user on this failing.
    } finally {
      tokenStore.clear();
      // Half-typed forms belong to the person who typed them.
      clearSessionDrafts();
      set({ user: null, isAuthenticated: false });
    }
  },

  // One restore per page load, however many times it is asked for. StrictMode
  // runs the caller's effect twice in development, and two refreshes racing on
  // one cookie look like a replayed token to the server — which revokes every
  // session, and the loser's failure here used to wipe the drafts with it.
  bootstrap: () =>
    (bootstrapping ??= (async () => {
      try {
        const session = await authApi.refresh();
        if (!session) {
          // No cookie at all — a normal first visit.
          tokenStore.clear();
          clearSessionDrafts();
          set({ user: null, isAuthenticated: false });
          return;
        }
        tokenStore.set(session.accessToken);
        set({ user: session.user, isAuthenticated: true });
      } catch (error) {
        tokenStore.clear();
        // Only a cookie the server refused (expired, revoked) ends the session
        // the drafts belong to. A rate limit, a 5xx or a dropped connection says
        // nothing about it, and drafts are keyed by user — signing back in as
        // the same person should find their half-finished form still there.
        if (error instanceof ApiRequestError && error.status === 401) {
          clearSessionDrafts();
        }
        set({ user: null, isAuthenticated: false });
      } finally {
        set({ isBootstrapping: false });
      }
    })()),

  clearSession: () => {
    tokenStore.clear();
    clearSessionDrafts();
    set({ user: null, isAuthenticated: false, isBootstrapping: false });
  },

  setUser: (user) => set({ user }),
}));

/**
 * Lets the axios interceptor tear down the session when a refresh finally
 * fails, without importing this store (which would be circular).
 */
tokenStore.onExpired(() => {
  useAuthStore.getState().clearSession();
});
